import React, { useRef, useState } from 'react';
import FabricCanvas from '../components/builder/FabricCanvas';
import ToolPanel from '../components/builder/ToolPanel';
import { Download, Smartphone, Info } from 'lucide-react';

const CANVAS_W = 320;
const CANVAS_H = 100;

export default function Texting() {
  const canvasRef = useRef(null);
  const [vcardData, setVcardData] = useState({
    name: 'John Smith',
    phone: '555-123-4567',
    email: 'john@company.com',
    company: 'Company Name',
    title: 'Job Title',
    website: 'company.com',
  });

  const exportPNG = () => {
    const url = canvasRef.current?.toDataURL();
    if (!url) {
      alert('Canvas is empty');
      return;
    }
    const a = document.createElement('a');
    a.href = url;
    a.download = 'text-signature.png';
    a.click();
  };

  const exportVCard = () => {
    const vcard = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${vcardData.name}`,
      `N:${vcardData.name.split(' ').reverse().join(';')};;;`,
      vcardData.phone ? `TEL;TYPE=CELL:${vcardData.phone}` : '',
      vcardData.email ? `EMAIL:${vcardData.email}` : '',
      vcardData.company ? `ORG:${vcardData.company}` : '',
      vcardData.title ? `TITLE:${vcardData.title}` : '',
      vcardData.website ? `URL:https://${vcardData.website.replace(/^https?:\/\//, '')}` : '',
      'END:VCARD',
    ].filter(Boolean).join('\r\n');

    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${vcardData.name.replace(/\s+/g, '_')}.vcf`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handle = (e) => setVcardData(d => ({ ...d, [e.target.name]: e.target.value }));

  const addNameToCanvas = () => {
    canvasRef.current?.addText(vcardData.name, { fontSize: 16, fontWeight: 'bold' });
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <Smartphone className="w-7 h-7 text-green-600" />
        <h1 className="text-2xl font-bold text-gray-900">Texting Signature</h1>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-6 flex gap-2 text-sm text-amber-800">
        <Info className="w-5 h-5 flex-shrink-0 mt-0.5" />
        <p>
          Create a small signature image for SMS/iMessage (320×100px) and export a vCard (.vcf)
          that recipients can tap to add you to their contacts.
        </p>
      </div>

      <div className="flex gap-6 flex-wrap">
        {/* Left: tools */}
        <div className="w-48 flex-shrink-0 space-y-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <ToolPanel canvasRef={canvasRef} />
          </div>
          <div className="space-y-2">
            <button
              onClick={addNameToCanvas}
              className="w-full flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm hover:bg-gray-50"
            >
              Add Name to Canvas
            </button>
            <button
              onClick={exportPNG}
              className="w-full flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700"
            >
              <Download className="w-4 h-4" /> Export PNG
            </button>
            <button
              onClick={exportVCard}
              className="w-full flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700"
            >
              <Download className="w-4 h-4" /> Export vCard (.vcf)
            </button>
          </div>
        </div>

        {/* Center: canvas */}
        <div className="flex-1 min-w-0">
          <div className="bg-gray-100 p-6 rounded-xl flex justify-center mb-3">
            <FabricCanvas ref={canvasRef} width={CANVAS_W} height={CANVAS_H} />
          </div>
          <p className="text-xs text-gray-400 text-center">
            {CANVAS_W}×{CANVAS_H}px · Optimized for SMS/iMessage display
          </p>
        </div>

        {/* Right: vCard data */}
        <div className="w-56 flex-shrink-0">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              vCard Contact Info
            </h3>
            <div className="space-y-2.5">
              {[
                { key: 'name', label: 'Full Name', placeholder: 'John Smith' },
                { key: 'title', label: 'Job Title', placeholder: 'Developer' },
                { key: 'company', label: 'Company', placeholder: 'Company Inc.' },
                { key: 'phone', label: 'Phone', placeholder: '555-123-4567' },
                { key: 'email', label: 'Email', placeholder: 'you@company.com' },
                { key: 'website', label: 'Website', placeholder: 'company.com' },
              ].map(({ key, label, placeholder }) => (
                <div key={key}>
                  <label className="block text-xs text-gray-500 mb-0.5">{label}</label>
                  <input
                    name={key}
                    value={vcardData[key]}
                    onChange={handle}
                    placeholder={placeholder}
                    className="w-full border border-gray-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
